import { CdkAccordionItem } from '@angular/cdk/accordion';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';

import { MatIcon } from '@angular/material/icon';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatTooltip } from '@angular/material/tooltip';
import { FileService } from '@ipg/ngx-frontend-toolset';
import { ImageInteractionAnswer } from '../../../../models/interactions.model';
import { HighlightVariablesPipe } from '../../../../pipes/highlight-variables.pipe';
import { AnswerTreeNode } from '../../../../views/interaction-editor/interaction-editor.component';
import { AvatarComponent } from '../../../avatar/avatar.component';
import { ActionListComponent } from '../action-list/action-list.component';
import { InteractionEditorStateService } from '../../services/interaction-editor-state.service';

@Component({
  selector: 'app-answer-list-item',
  imports: [
    CdkAccordionItem,
    CdkDrag,
    CdkDragHandle,
    MatIcon,
    MatButtonToggleModule,
    MatTooltip,
    AvatarComponent,
    ActionListComponent,
    HighlightVariablesPipe,
  ],
  templateUrl: './answer-list-item.component.html',
  styleUrl: './answer-list-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnswerListItemComponent {
  private readonly _fileService = inject(FileService);
  private readonly _accordionItem = viewChild(CdkAccordionItem);
  private readonly _editorState = inject(InteractionEditorStateService);

  public readonly data = input.required<AnswerTreeNode>();
  public readonly index = input<number>();
  public readonly draggable = input(false, { transform: booleanAttribute });
  public readonly selected = input(false, { transform: booleanAttribute });
  public readonly clientId = input<string | null>(null);
  public readonly totalAnswers = input(0);

  public readonly selectionChange = output();

  constructor() {
    effect(() => {
      if (!this.selected() && this._accordionItem()?.expanded) {
        this._accordionItem()?.close();
      }
    });
  }

  protected getFileUrl(fileId: string): string {
    return this._fileService.get(fileId).href;
  }

  protected asImageAnswer(answer: AnswerTreeNode) {
    return answer as AnswerTreeNode & { type: 'image' };
  }

  protected onFullScreenToggle(checked: boolean): void {
    const answer = this.data();
    if (answer.type !== 'image') return;

    const imageAnswer = answer as ImageInteractionAnswer & { children: unknown[] };

    // Select this answer so the update pipeline can find it
    this._editorState.selectedAnswerId.set(answer._id);

    this._editorState.emitEvent({
      entity: 'answers',
      action: 'update',
      id: answer._id,
      type: 'image',
      value: { mediaId: imageAnswer.value._id },
      fullScreen: checked,
    });
  }
}
